import widgetTransformer from '@nykaa/deals-transformer';
import {
  getPrimaryCategories,
  getCallOutNudgeText,
  getCallOutTextV2,
} from '@nykaa/product-card-shared/utils';
import { transformPriceFluctuationData } from '@nykaa/ui-kit/component/PriceFluctuationNudge/utils';
import { transformAdPlatformData } from '@nykaa/listing-shared/transformer/index';
import { transformListingGuides } from '@nykaa/listing-shared/transformer/filter';
import { transformRelatedSearches } from '@nykaa/listing-shared/transformer/relatedSearches';
import {
  Filter,
  InlineFilter,
  InlineFiltersData,
  ListingProduct,
} from 'store/categoryListing/types';

import { transformListingFilters } from 'shared/helpers/filters/transformer';
import { getRoundedDiscount } from 'shared/helpers/utils/price';
import { getVariantType } from 'shared/helpers/utils/variantType';
import transformTipTile from 'store/categoryListing/helpers/transformTipTile';
import { sanitizeQuery } from './helpers';
import { ListingData, RedirectionData } from './types';
import { ListingItemObjectType, TipTile } from './../categoryListing/types';
import {
  transformCustomTag,
  transformInlineFiltersV2,
  transformEddInfo,
} from 'store/categoryListing/transformer';
import { ADD_TO_BAG_TEXT } from 'shared/constants';
import { PagePrefixes } from 'constants/pagePrefixes';
import transformPriceRevealData from '@nykaa/listing-shared/transformer/priceReveal';
import {
  getCohortSaleData,
  getCohortSaleDataV2,
  getUpdatedTemplateV2,
} from 'helpers/utils/store/priceReaveal';
// TODO: change the location of time

export const transformSearchListingProducts = (
  products: any[],
  assetsBucket: any
): (ListingProduct | TipTile)[] => {
  if (!(products && Array.isArray(products))) {
    return [];
  }

  const transformedProducts: (ListingProduct | TipTile)[] = [];

  products.forEach((product) => {
    const curationTagsurl =
      assetsBucket &&
      product?.curation_tags?.map((tag: string) => assetsBucket[tag]);
    const leftTagUrl = assetsBucket && assetsBucket[product?.left_tags];
    const secondaryTagUrl =
      assetsBucket && assetsBucket[product?.secondary_tags];
    const secondaryTagV2 = product?.secondary_tags_v2;
    const secondaryTagV2Url = secondaryTagV2 && assetsBucket?.[secondaryTagV2];

    const { object_type: objectType } = product;
    const cohortSaleTemplateV2 = getUpdatedTemplateV2(
      product?.cohort_sale_template,
      product?.cohort_sale_template_v2
    );
    if (objectType === ListingItemObjectType.PRODUCT) {
      const transformedProduct: ListingProduct = {
        brandIds: product.brand_ids,
        brandName: product.brand_name?.[0] || '',
        buttonText: product.button_text || ADD_TO_BAG_TEXT,
        newButtonText: product.button_text_new,
        categoryIds: product.category_ids,
        childId: product.id,
        discount: getRoundedDiscount(product.discount),
        dynamicTags: product.dynamic_tags,
        newTags: transformCustomTag(product.pdt_tags),
        id: product.id,
        imageUrl: product.image_url,
        inStock: product.in_stock,
        mrp: product.price,
        name: product.name,
        productTitle: product.product_title,
        offersCount: product.offer_count,
        onlyWishlistButton: !!product.show_wishlist_button,
        parentId: product.parent_id,
        price: product.final_price,
        primaryCategories: getPrimaryCategories(product.primary_categories),
        productId: product.id,
        proFlag: product.pro_flag,
        quantity: product.quantity,
        rating: product.rating,
        ratingCount: product.rating_count,
        formattedRatingCount: product.formatted_rating_count,
        sku: product.sku || '',
        slug: product.slug,
        title: product.name,
        type: product.type,
        variantCount: product.option_count,
        variantType: getVariantType(product.configurable_type).toLowerCase(),
        isBackorder: !!product.is_backorder,
        isLux: Number(product.is_luxe) || 0,
        offer: product.offer_message || '',
        offerId: product.offer_id || '',
        offerColor: product.plp_offer_color,
        showOffer: product.plp_offer,
        packSize: product.pack_size,
        isMini: product?.is_mini || false,
        tracking_metadata: product.tracking_metadata,
        prData: transformPriceRevealData(
          product.pr_data,
          product.sale_template
        ),
        isPrEnabled: product.pr_enabled_raw || false,
        isEarlyAccess: product.early_access || false,
        isFlashSale: product.flash_sale || false,
        callOutsTemplate: getCallOutNudgeText(product.call_outs_template),
        cohortSaleTemplate: getCohortSaleData(product.cohort_sale_template),
        cohortSaleTemplateV2: getCohortSaleDataV2(
          cohortSaleTemplateV2,
          assetsBucket
        ),
        urgencyTemplate: transformPriceFluctuationData(
          product.urgency_template
        ),
        isBestPriceLoaded: false,
        primaryTags: curationTagsurl,
        leftTags: leftTagUrl,
        leftTagText: product?.left_tags || '',
        secondaryTags: secondaryTagUrl,
        callOutsTemplateV2: getCallOutTextV2(product.call_outs_template_v2),
        siblingColourCodes: product.sibling_colour_codes || [],
        disablePriveProduct: product.disable_prive_product || false,
        eddInfo: transformEddInfo(product.edd_info, assetsBucket?.qc),
        plpPdpBridge: product.plp_pdp_bridge || [],
        secondaryTagV2: secondaryTagV2Url,
        productNudges: product.product_nudges || {},
      };

      transformedProducts.push(transformedProduct);
    } else if (objectType === ListingItemObjectType.TIP_TILE) {
      const transformedTipTile = transformTipTile(product);

      if (transformedTipTile) {
        transformedProducts.push(transformedTipTile);
      }
    }
  });

  return transformedProducts;
};

const transformFilters = (inlineFilter: InlineFilter) => {
  if (inlineFilter) {
    const {
      bg_color,
      is_multi_select,
      redirect_filter,
      redirect_text,
      title,
      type,
      values,
      name,
      position,
    } = inlineFilter;
    return {
      bgColor: bg_color || '',
      isMultiSelect: is_multi_select || false,
      redirectFilter: redirect_filter || '',
      redirectText: redirect_text || '',
      title: title || '',
      type: type || '',
      name: name || '',
      position: position || null,
      values:
        values?.map((filter: Filter) => ({
          name: filter.name || '',
          id: filter.id || '',
          score: filter.score || '',
        })) || [],
    };
  }
  return {};
};

const transformInlineFilters = (inlineFilters: InlineFiltersData) => {
  const inlineFilterKeys = Object.keys(inlineFilters);
  const transformedInlineFilter = { ...inlineFilters };

  inlineFilterKeys?.forEach((key: string) => {
    transformedInlineFilter[key] = transformFilters(inlineFilters[key]);
  });
  return transformedInlineFilter;
};

export const transformSearchListingData = async (
  rawData: any
): Promise<ListingData | RedirectionData> => {
  const {
    redirect,
    title = '',
    subtitle = '',
    image_url: imageUrl = '',
    redirection_type = '',
    deeplink_url: deeplink = '',
    no_result_page_info: noResultPageInfoRaw = {},
    bottom_sheet = false,
    has_edd: hasEdd = false,
  } = rawData;

  if (redirect && redirect.value) {
    /**
     * 💡 Any of the other fields do not come in case of redirect
     * So, we just return with necessary data and avoid
     * trying to process the other information that just isn't there
     */
    return {
      isRedirect: true,
      redirectUrl: redirect.value,
      title,
      subtitle,
      imageUrl,
      type: redirection_type,
      deeplink,
      bottomSheet: bottom_sheet && bottom_sheet === 'true' ? true : false,
    };
  }

  // Handling suggestions
  let recommendedSearch = '';
  if (rawData.suggestions) {
    const suggestedSearch = rawData.suggestions.suggestion_word;

    if (suggestedSearch && suggestedSearch.trim() !== '') {
      recommendedSearch = sanitizeQuery(suggestedSearch);
    }
  }
  const searchTerm = rawData?.did_you_mean?.original_word || '';
  const didYouMean = rawData?.did_you_mean?.dym_word || '';

  // Handling "Top banners"
  let widgets = [];
  const topOffersFromRawData = rawData.top_offers; // ? Why showing as topOffers

  if (topOffersFromRawData && topOffersFromRawData.length > 0) {
    const transformedData = await widgetTransformer(
      topOffersFromRawData,
      false,
      __PLATFORM__,
      PagePrefixes.SEARCH
    );
    widgets = transformedData ? transformedData.widgets : [];
  }

  // Handling of SEO data
  const seoData = {
    footerContent: rawData.footer_content || '',
    h1Title: rawData.h1 || '',
    metaDescription: rawData.meta_description || '',
    metaKeywords: rawData.meta_keywords || '',
    metaTitle: rawData.title || '',
  };

  const noResultPageInfo = {
    pageType: noResultPageInfoRaw['page-type'] || '',
    pageData: noResultPageInfoRaw['page-data'] || '',
    emptyStateInfo: {
      title: noResultPageInfoRaw?.empty_state_info?.title || '',
      subTitle: noResultPageInfoRaw?.empty_state_info?.sub_title || '',
    },
  };

  const transformedFilters = transformListingFilters(rawData.filters) || [];
  const assetsBucket = rawData.assets_bucket;

  return {
    isApiFailed: false, // this is added just to handle the linting issue.
    recommendedSearch,
    filters: transformedFilters,
    messageForApproxResults: rawData.ui_message || '',
    offset: rawData.offset || 0,
    products: transformSearchListingProducts(rawData.products, assetsBucket),
    seoData,
    showingApproximateResults:
      rawData.no_results_found__showing_approximate_results || false,
    sort: rawData.sort,
    stopFurtherCall: !!rawData.stop_further_call, // convert to boolean
    totalFound: rawData.total_found,
    widgets,
    trackingMetadata: rawData.tracking_metadata || {},
    guides: transformListingGuides(rawData.guides, transformedFilters),
    guidesV2: transformListingGuides(rawData.guides_v2, transformedFilters),
    newUi: rawData.new_ui || false,
    reviewRatingVisible: rawData.review_rating_visible,
    ctaVisible: rawData.cta_visible,
    imageAspectRatio: rawData.image_aspect_ratio || 1,
    queryExpansion: {
      term: rawData?.query_expansion_term,
      type: rawData?.query_expansion_type,
    },
    didYouMean: {
      didYouMean,
      searchTerm,
    },
    noResultPageInfo,
    inlineFilters: transformInlineFilters(rawData.inline_guides || {}),
    inlineFiltersV2: transformInlineFiltersV2(rawData.inline_guides_v2 || {}),
    inlineWidgetConfig: transformAdPlatformData(rawData.ad_platform_data),
    relatedSearches: transformRelatedSearches(
      rawData.related_searches_data || []
    ),
    entityCoverage: rawData?.entity_coverage,
    entityType: rawData?.entity_type,
    entityValues: rawData?.entity_values,
    hasEdd,
    visualFilters: rawData?.visual_filters || [],
  };
};

export const transformPageData = (rawData: any) => ({
  products: transformSearchListingProducts(
    rawData.products,
    rawData.assets_bucket
  ),
  stopFurtherCall: !!rawData.stop_further_call,
});
